// DashboardProfile.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { signOut, updatePassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc, getDoc, writeBatch } from 'firebase/firestore';
import Navbar from '../../components/Navbar/Navbar';
import ProfilePictureUpload from '../../components/ProfilePictureUpload/ProfilePictureUpload';
import SetUsernameModal from '../../pages/SetUsername/SetUsername';
import EditQueryModal from '../../components/EditQuery/EditQueryModal'; // Import the EditQueryModal component
import { fetchUserPoints, getUserLevel } from '../../utils/pointsSystem'; // Import fetchUserPoints and getUserLevel
import './DashboardProfile.css';
import { Link, useParams } from 'react-router-dom';
import { ThumbsUp, MessageCircle, Scale, Gavel, FileText, Trash2, Edit3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function DashboardProfile() {
  const { userId } = useParams(); // Get userId from URL params
  const { currentUser, userData, loading } = useAuth();
  const [profileUser, setProfileUser] = useState(null); // User whose profile is being viewed
  const [isOwnProfile, setIsOwnProfile] = useState(false); // Flag to check if viewing own profile
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [queries, setQueries] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [editQuery, setEditQuery] = useState(null); // Add state for edit query
  const [userPoints, setUserPoints] = useState(0);
  const [userLevel, setUserLevel] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingQueries, setLoadingQueries] = useState(true);
  const [expandedFields, setExpandedFields] = useState({
    username: false,
    password: false
  });

  // Fetch user dockets from cases collection (excludes legacy AskAway queries)
  const fetchUserQueries = useCallback(async (uid) => {
    try {
      setLoadingQueries(true);
      const casesRef = collection(db, "cases");
      const q = query(casesRef, where("authorId", "==", uid));
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQueries(results);
      setLoadingQueries(false);
    } catch (error) {
      console.error("Error fetching dockets:", error);
      setLoadingQueries(false);
    }
  }, []);

  // New function to fetch profile user data
  const fetchProfileUserData = useCallback(async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        setProfileUser(userDoc.data());
        
        // Fetch user's points
        const points = await fetchUserPoints(uid);
        setUserPoints(points);
        
        // Fetch user level
        const level = getUserLevel(points);
        setUserLevel(level);
        
        // Fetch their queries
        await fetchUserQueries(uid);
      } else {
        console.error("User document does not exist");
        // Handle user not found case
      }
    } catch (error) {
      console.error("Error fetching profile user data:", error);
    } finally {
      setLoadingProfile(false); // Make sure loading state is set to false even if there's an error
    }
  }, [fetchUserQueries]);

  // Use the auth context to determine if user is logged in
  useEffect(() => {
    setLoadingProfile(true);
    
    // If URL contains a userId, we're viewing someone else's profile
    if (userId) {
      setIsOwnProfile(currentUser && userId === currentUser.uid);
      fetchProfileUserData(userId);
    } 
    // If no userId in URL and we have current user, we're viewing our own profile
    else if (currentUser) {
      setIsOwnProfile(true);
      setProfileUser(currentUser);
      
      if (!currentUser.displayName) {
        setShowUsernameModal(true);
      } else {
        setNewUsername(currentUser.displayName);
      }
      
      fetchUserQueries(currentUser.uid)
        .then(() => {
          return fetchUserPoints(currentUser.uid);
        })
        .then((points) => {
          setUserPoints(points);
          
          // Fetch user level
          const level = getUserLevel(points);
          setUserLevel(level);
        })
        .catch(error => {
          console.error("Error in useEffect data fetching:", error);
        })
        .finally(() => {
          setLoadingProfile(false);
        });
    } else {
      // No current user and no userId
      setLoadingProfile(false);
    }
  }, [userId, currentUser, fetchUserQueries, fetchProfileUserData]);

  // Fetch user data when the user is available or when points are updated
  useEffect(() => {
    const fetchUserPointsAndLevel = async () => {
      if (currentUser?.uid) {
        try {
          const points = await fetchUserPoints(currentUser.uid);
          setUserPoints(points);
          
          // Get user's level
          const level = getUserLevel(points);
          setUserLevel(level);
        } catch (err) {
          console.error("Error fetching user data:", err);
        }
      }
    };
    
    fetchUserPointsAndLevel();
  }, [currentUser, userData]);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.reload();
  };

  const handleUpdateUsername = async () => {
    try {
      if (!newUsername.trim() || newUsername === currentUser.displayName) {
        setMessage('Please enter a new username');
        setTimeout(() => setMessage(''), 3000);
        return;
      }

      setMessage('Updating username across all content...');
      
      // 1. Update Firebase Auth profile
      await updateProfile(auth.currentUser, { displayName: newUsername });
      
      // 2. Update user document in Firebase
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        name: newUsername, // Update the name field in Firebase
        username: newUsername, // Also update username field if it exists
        updatedAt: new Date()
      });
      
      // 3. Update queries
      const userQueriesRef = collection(db, "queries");
      const q = query(userQueriesRef, where("createdBy", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      // Use a separate batch for queries
      const queriesBatch = writeBatch(db);
      let batchCount = 0;
      
      querySnapshot.forEach((doc) => {
        queriesBatch.update(doc.ref, { createdByName: newUsername });
        batchCount++;
      });
      
      // 4. Find and update all comments
      const allQueriesRef = collection(db, "queries");
      const allQueriesSnapshot = await getDocs(allQueriesRef);
      
      let totalCommentsUpdated = 0;
      let totalRepliesUpdated = 0;
      
      // Process each query one by one
      for (const queryDoc of allQueriesSnapshot.docs) {
        const queryId = queryDoc.id;
        
        // Get comments by this user
        const commentsRef = collection(db, "queries", queryId, "comments");
        const userCommentsQuery = query(commentsRef, where("createdBy", "==", currentUser.uid));
        const commentsSnapshot = await getDocs(userCommentsQuery);
        
        if (commentsSnapshot.size > 0) {
          // Use a separate batch for each query's comments
          const commentsBatch = writeBatch(db);
          let commentBatchCount = 0;
          
          commentsSnapshot.forEach((commentDoc) => {
            commentsBatch.update(commentDoc.ref, { commenterName: newUsername });
            commentBatchCount++;
          });
          
          if (commentBatchCount > 0) {
            await commentsBatch.commit();
            totalCommentsUpdated += commentBatchCount;
            console.log(`Updated ${commentBatchCount} comments in query ${queryId}`);
          }
        }
        
        // For each comment (by any user), find replies from this user
        const allCommentsRef = collection(db, "queries", queryId, "comments");
        const allCommentsSnapshot = await getDocs(allCommentsRef);
        
        for (const commentDoc of allCommentsSnapshot.docs) {
          const commentId = commentDoc.id;
          
          // Get replies by this user
          const repliesRef = collection(db, "queries", queryId, "comments", commentId, "replies");
          const repliesSnapshot = await getDocs(repliesRef);
          
          if (repliesSnapshot.size > 0) {
            // Use a separate batch for each comment's replies
            const repliesBatch = writeBatch(db);
            let replyBatchCount = 0;
            
            repliesSnapshot.forEach((replyDoc) => {
              if (replyDoc.data().createdBy === currentUser.uid) {
                repliesBatch.update(replyDoc.ref, { commenterName: newUsername });
                replyBatchCount++;
              }
            });
            
            if (replyBatchCount > 0) {
              await repliesBatch.commit();
              totalRepliesUpdated += replyBatchCount;
              console.log(`Updated ${replyBatchCount} replies in comment ${commentId}`);
            }
          }
        }
      }
      
      // Refresh displayed queries
      await fetchUserQueries(currentUser.uid);
      
      setMessage(`Username updated successfully across ${batchCount} queries, ${totalCommentsUpdated} comments, and ${totalRepliesUpdated} replies.`);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error("Error updating username:", err);
      setMessage(`Error updating username: ${err.message}`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleChangePassword = async () => {
    try {
      await updatePassword(auth.currentUser, newPassword);
      setMessage('Password updated successfully.');
      setNewPassword('');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.message);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleProfilePicUpdate = async (url) => {
    try {
      setMessage('Updating profile picture across all content...');
      
      // 1. Update Firebase Auth profile
      await updateProfile(auth.currentUser, { photoURL: url });
      
      // 2. Update user document in Firebase
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        photoURL: url, // Add the profile picture URL to the user document
        updatedAt: new Date()
      });
      
      // 3. Update queries
      const userQueriesRef = collection(db, "queries");
      const q = query(userQueriesRef, where("createdBy", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      // Use a separate batch for queries
      const queriesBatch = writeBatch(db);
      let batchCount = 0;
      
      querySnapshot.forEach((doc) => {
        queriesBatch.update(doc.ref, { createdByPhoto: url });
        batchCount++;
      });
      
      if (batchCount > 0) {
        await queriesBatch.commit();
        console.log(`Updated ${batchCount} queries with new profile picture`);
      }
      
      // 4. Find and update all comments
      const allQueriesRef = collection(db, "queries");
      const allQueriesSnapshot = await getDocs(allQueriesRef);
      
      let totalCommentsUpdated = 0;
      let totalRepliesUpdated = 0;
      
      // Process each query one by one
      for (const queryDoc of allQueriesSnapshot.docs) {
        const queryId = queryDoc.id;
        
        // Get comments by this user
        const commentsRef = collection(db, "queries", queryId, "comments");
        const userCommentsQuery = query(commentsRef, where("createdBy", "==", currentUser.uid));
        const commentsSnapshot = await getDocs(userCommentsQuery);
        
        if (commentsSnapshot.size > 0) {
          // Use a separate batch for each query's comments
          const commentsBatch = writeBatch(db);
          let commentBatchCount = 0;
          
          commentsSnapshot.forEach((commentDoc) => {
            commentsBatch.update(commentDoc.ref, { commenterPhoto: url });
            commentBatchCount++;
          });
          
          if (commentBatchCount > 0) {
            await commentsBatch.commit();
            totalCommentsUpdated += commentBatchCount;
            console.log(`Updated ${commentBatchCount} comments in query ${queryId}`);
          }
        }
        
        // For each comment (by any user), find replies from this user
        const allCommentsRef = collection(db, "queries", queryId, "comments");
        const allCommentsSnapshot = await getDocs(allCommentsRef);
        
        for (const commentDoc of allCommentsSnapshot.docs) {
          const commentId = commentDoc.id;
          
          // Get replies by this user
          const repliesRef = collection(db, "queries", queryId, "comments", commentId, "replies");
          const repliesSnapshot = await getDocs(repliesRef);
          
          if (repliesSnapshot.size > 0) {
            // Use a separate batch for each comment's replies
            const repliesBatch = writeBatch(db);
            let replyBatchCount = 0;
            
            repliesSnapshot.forEach((replyDoc) => {
              if (replyDoc.data().createdBy === currentUser.uid) {
                repliesBatch.update(replyDoc.ref, { commenterPhoto: url });
                replyBatchCount++;
              }
            });
            
            if (replyBatchCount > 0) {
              await repliesBatch.commit();
              totalRepliesUpdated += replyBatchCount;
              console.log(`Updated ${replyBatchCount} replies in comment ${commentId}`);
            }
          }
        }
      }
      
      // Refresh displayed queries
      await fetchUserQueries(currentUser.uid);
      
      setMessage(`Profile picture updated successfully across ${batchCount} queries, ${totalCommentsUpdated} comments, and ${totalRepliesUpdated} replies.`);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error("Error updating profile picture:", err);
      setMessage(`Error updating profile picture: ${err.message}`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Open edit modal
  const openEditModal = (queryItem) => {
    setEditQuery(queryItem);
  };

  // Delete docket from Firestore
  const handleDelete = async (queryId) => {
    if (window.confirm("Are you sure you want to withdraw this docket from The Kangaroo Court?")) {
      try {
        await deleteDoc(doc(db, "cases", queryId));
        try { await deleteDoc(doc(db, "queries", queryId)); } catch (_) {}
        setQueries(prev => prev.filter(q => q.id !== queryId));
      } catch (err) {
        console.error("Error withdrawing docket:", err);
      }
    }
  };

  // Toggle expanded fields
  const toggleField = (field) => {
    setExpandedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Add this section for the badges display component
  const UserBadges = ({ badges, activeBadge }) => {
    if (!badges || badges.length === 0) {
      return (
        <div className="user-badges">
          <p>No badges earned yet.</p>
        </div>
      );
    }
    
    return (
      <div className="user-badges">
        <div className="badges-container">
          {badges.map((badge, index) => (
            <div 
              key={index} 
              className={`profile-badge ${badge.id === activeBadge ? 'active' : ''}`}
              title={badge.name}
            >
              <span className="badge-icon">{badge.icon || badge.emoji || '🏆'}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading || loadingProfile) {
    return (
      <div className="dashboard-container">
        <Navbar />
        <div className="dashboard-content">
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser && !userId) {
    return (
      <div className="dashboard-container">
        <Navbar />
        <div className="dashboard-content">
          <div className="profile-error">
            <h2>Not Logged In</h2>
            <p>Please log in to view your dashboard.</p>
            <Link to="/login" className="back-link">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  // If userId is provided but profileUser couldn't be loaded
  if (userId && !profileUser && !loadingProfile) {
    return (
      <div className="dashboard-container">
        <Navbar />
        <div className="dashboard-content">
          <div className="profile-error">
            <h2>User not found</h2>
            <p>The requested profile could not be found.</p>
            <Link to="/" className="back-link">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navbar />
      <div className="dashboard-content">
        <div className="dashboard-header">
          {isOwnProfile ? (
            <>
              <div className="user-welcome">
                <h2>Welcome, {currentUser?.displayName || 'Juror'}</h2>
                <div className="juror-title-row">
                  <span className="standing-badge-inline">
                    {profileUser?.isAdmin ? '⚖️ Chief Justice' : '⚖️ Admitted Juror'}
                  </span>
                  <span className="chips-counter-inline">
                    🪙 {userPoints} Court Chips
                  </span>
                  {userLevel && (
                    <span className="level-pill-inline">
                      Rank {userLevel.level}: {userLevel.name}
                    </span>
                  )}
                </div>
              </div>
              <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
            </>
          ) : (
            <>
              <div className="user-welcome">
                <h2>{profileUser?.displayName || profileUser?.name || 'Juror'}'s Chambers</h2>
                <div className="juror-title-row">
                  <span className="standing-badge-inline">
                    {profileUser?.isAdmin ? '⚖️ Chief Justice' : '⚖️ Admitted Juror'}
                  </span>
                  <span className="chips-counter-inline">
                    🪙 {userPoints} Court Chips
                  </span>
                </div>
              </div>
              <Link to="/" className="back-btn">← Back to Docket</Link>
            </>
          )}
        </div>

        <div className="dashboard-tabs">
          <button 
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`} 
            onClick={() => setActiveTab('profile')}
          >
            <Scale size={16} /> Juror Record & Credentials
          </button>
          <button 
            className={`tab-button ${activeTab === 'queries' ? 'active' : ''}`} 
            onClick={() => setActiveTab('queries')}
          >
            <FileText size={16} /> {isOwnProfile ? 'My Filed Dockets' : `${profileUser?.displayName || 'Juror'}'s Dockets`} ({queries.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'profile' && (
            <div className="profile-tab">
              <div className="user-profile-section">
                <div className="profile-hero-card">
                  <div className="profile-picture-wrapper">
                    {profileUser?.photoURL || userData?.photoURL ? (
                      <img
                        src={profileUser?.photoURL || userData?.photoURL}
                        alt="Profile"
                        className="profile-picture-judicial"
                      />
                    ) : (
                      <div className="profile-picture-placeholder">
                        {(profileUser?.displayName || profileUser?.name || profileUser?.email || 'J').charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isOwnProfile && (
                      <ProfilePictureUpload onUpdate={handleProfilePicUpdate} 
                        onUploading={(isUploading) => setUploading(isUploading)}
                        onProgress={(progress) => setUploadProgress(progress)}
                      />
                    )}
                    {uploading && (
                      <div className="upload-progress">
                        <progress value={uploadProgress} max={100}></progress>
                        <span>{Math.round(uploadProgress)}%</span>
                      </div>
                    )}
                  </div>

                  <div className="user-details-judicial">
                    <div className="user-name-row">
                      <h2 className="dashboard-username">
                        {profileUser?.displayName || profileUser?.name || profileUser?.email}
                      </h2>
                      {profileUser?.displayBadge && <span className="dashboard-user-badge">{profileUser.displayBadge}</span>}
                    </div>
                    <p className="dashboard-email">{profileUser?.email || 'No email attached'}</p>
                    
                    <div className="user-level-display">
                      <div className="level-badge">
                        Level {userLevel?.level || '1'}
                      </div>
                      <span className="level-name">{userLevel?.name || 'Novice Juror'}</span>
                      <div className="points-display">
                        <span className="points-value">{userPoints}</span> chips
                      </div>
                    </div>
                  </div>
                </div>

                {isOwnProfile && (
                  <>
                    {/* Judicial Standing Box */}
                    <div className="judicial-standing-card">
                      <div className="standing-header">
                        <span className="standing-title">⚖️ Judicial Standing & Bar Record</span>
                        <span className="standing-badge">{profileUser?.isAdmin ? 'Chief Justice' : 'Admitted Juror'}</span>
                      </div>
                      <div className="standing-metrics-row">
                        <div className="standing-metric">
                          <span className="m-val">{userPoints}</span>
                          <span className="m-lbl">Court Chips</span>
                        </div>
                        <div className="standing-metric">
                          <span className="m-val">{profileUser?.contemptBadges?.length || 0}</span>
                          <span className="m-lbl">Active Contempts</span>
                        </div>
                      </div>
                      {profileUser?.contemptBadges && profileUser.contemptBadges.length > 0 ? (
                        <div className="standing-badges-list">
                          <span className="badges-label">Contempt Penalties:</span>
                          {profileUser.contemptBadges.map((b, i) => (
                            <span key={i} className="contempt-tag">{b}</span>
                          ))}
                        </div>
                      ) : (
                        <p className="clean-record-text">✓ Clean record. No contempt penalties pending before the Court.</p>
                      )}
                      <Link to="/rewards" className="btn-visit-bail-store">Visit Bail & Contempt Store →</Link>
                    </div>
                    
                    {/* Profile fields for changing username/password */}
                    <div className="profile-credentials-card">
                      <h3 className="credentials-card-title">Security & Chambers Identity</h3>
                      {message && <div className="message">{message}</div>}

                      <div className="profile-field-item">
                        <div 
                          className="field-header" 
                          onClick={() => toggleField('username')}
                        >
                          <span className="field-title">Change Court Pseudonym</span>
                          <span className={`toggle-icon ${expandedFields.username ? 'expanded' : ''}`}>
                            ▼
                          </span>
                        </div>
                        {expandedFields.username && (
                          <div className="field-content">
                            <input
                              type="text"
                              value={newUsername}
                              onChange={(e) => setNewUsername(e.target.value)}
                              placeholder="Enter new pseudonym"
                              className="profile-input"
                            />
                            <button onClick={handleUpdateUsername} className="update-button">
                              Update Username
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="profile-field-item">
                        <div 
                          className="field-header" 
                          onClick={() => toggleField('password')}
                        >
                          <span className="field-title">Update Chambers Password</span>
                          <span className={`toggle-icon ${expandedFields.password ? 'expanded' : ''}`}>
                            ▼
                          </span>
                        </div>
                        {expandedFields.password && (
                          <div className="field-content">
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              className="profile-input"
                            />
                            <button onClick={handleChangePassword} className="update-button">
                              Update Password
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {userData?.badges && userData.badges.length > 0 && (
                <div className="user-badges">
                  <h3>Earned Juror Insignias</h3>
                  <UserBadges badges={userData.badges} activeBadge={userData.activeBadge} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'queries' && (
            <div className="queries-section">
              <div className="queries-section-header">
                <div>
                  <h2 className="section-title">
                    <Gavel size={20} className="title-icon" />
                    {isOwnProfile ? 'My Filed Dockets' : `${profileUser?.displayName || 'Juror'}'s Filings`}
                  </h2>
                  <p className="section-subtitle">Tribunal petitions, workplace disasters, and peer grievances on record.</p>
                </div>
                {isOwnProfile && (
                  <Link to="/post" className="btn-file-case">+ File New Case</Link>
                )}
              </div>

              {loadingQueries ? (
                <div className="queries-loading">
                  <div className="loading-spinner"></div>
                  <p>Retrieving case records from docket...</p>
                </div>
              ) : queries.length === 0 ? (
                <div className="no-queries-card">
                  <FileText size={48} className="empty-icon" />
                  <h3>No Dockets on Record</h3>
                  <p>No grievances or disasters have been filed before the Kangaroo Court under this identity.</p>
                  {isOwnProfile && (
                    <Link to="/post" className="btn-file-case-action">File Your First Case →</Link>
                  )}
                </div>
              ) : (
                <div className="queries-list">
                  {queries.map((q) => (
                    <div key={q.id} className="query-card-judicial">
                      <div className="query-content">
                        <div className="query-meta-top">
                          <span className="query-discipline-badge">
                            {q.discipline || q.category || 'Tribunal Case'}
                          </span>
                          <span className="query-date-badge">
                            {q.createdAt?.toDate ? q.createdAt.toDate().toLocaleDateString() : 'Active Docket'}
                          </span>
                        </div>
                        <h3 className="query-title">
                          <Link to={`/query/${q.id}`}>{q.title}</Link>
                        </h3>
                        <p className="query-description">
                          {q.description?.slice(0, 180)}{q.description?.length > 180 ? '...' : ''}
                        </p>
                        <div className="query-stats-row">
                          <span className="stat-pill">
                            <ThumbsUp size={14} /> {q.voteCount || 0} Jury Votes
                          </span>
                          <span className="stat-pill">
                            <MessageCircle size={14} /> {q.commentCount || 0} Deliberations
                          </span>
                          {q.verdict && (
                            <span className="stat-verdict-pill">
                              ⚖️ Verdict: {q.verdict}
                            </span>
                          )}
                        </div>
                      </div>
                      {isOwnProfile && (
                        <div className="query-actions-panel">
                          <button onClick={() => openEditModal(q)} className="action-btn edit-action-btn" title="Edit Case Details">
                            <Edit3 size={15} /> Edit
                          </button>
                          <button onClick={() => handleDelete(q.id)} className="action-btn delete-action-btn" title="Withdraw Docket">
                            <Trash2 size={15} /> Withdraw
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showUsernameModal && (
        <SetUsernameModal 
          onClose={() => setShowUsernameModal(false)} 
        />
      )}

      {editQuery && (
        <EditQueryModal 
          query={editQuery}
          onClose={() => setEditQuery(null)}
          onSave={() => fetchUserQueries(currentUser.uid)}
        />
      )}
    </div>
  );
}

export default DashboardProfile;
